import { isNative } from "@/lib/platform";
import { createDeviceIdGenerator } from "../shared/device-id-generator";
import { DefaultNotificationHandler } from "../shared/notification-handler";
import { ApiTokenManager } from "../shared/token-manager";
import type { PushNotificationService } from "../types";
import { CapacitorPushService } from "./capacitor-service";
import { FirebasePushService } from "./firebase-service";

/**
 * Coordinates push notifications across platforms
 * Automatically selects the appropriate service based on platform
 */
export class PushNotificationCoordinator implements PushNotificationService {
  private service: PushNotificationService;

  constructor() {
    const tokenManager = new ApiTokenManager();
    const notificationHandler = new DefaultNotificationHandler();
    const deviceIdGenerator = createDeviceIdGenerator();

    this.service = isNative()
      ? new CapacitorPushService(tokenManager, notificationHandler, deviceIdGenerator)
      : new FirebasePushService(tokenManager, notificationHandler, deviceIdGenerator);
  }

  async requestPermission(): Promise<boolean> {
    return this.service.requestPermission();
  }

  async getToken(): Promise<string | null> {
    return this.service.getToken();
  }

  getCurrentToken(): string | null {
    return this.service.getCurrentToken();
  }

  isSupported(): boolean {
    return this.service.isSupported();
  }

  async getPermissionStatus() {
    return this.service.getPermissionStatus();
  }

  async onUserLogin(): Promise<void> {
    return this.service.onUserLogin();
  }

  async onUserLogout(): Promise<void> {
    return this.service.onUserLogout();
  }

  async onAccountSwitch(): Promise<void> {
    return this.service.onAccountSwitch();
  }

  async removeAllDeliveredNotifications(): Promise<void> {
    return this.service.removeAllDeliveredNotifications?.();
  }

  async getDeliveredNotifications(): Promise<any[]> {
    return this.service.getDeliveredNotifications?.() || [];
  }
}
